// A description of a scope value's shape, computed at runtime. The things that
// produce a scope (the read-only Proxy objects in no-code/utils/repeatables,
// which intercept property reads) build this description from their own live
// config and attach it under the CM_SCOPE_SCHEMA key. The CodeMirror editor then
// reads that description to generate accurate `declare const` TypeScript types
// for autocomplete. This replaces shipping a hand-written `.d.ts` file: because
// the shape is read off the proxy itself at runtime, it always matches whatever
// the proxy actually exposes.
//
// This module MUST NOT import anything (no editor, React, or platform imports).
// `instancesAccessor` imports it, and that code runs inside published matrix
// apps.
export type ScopeSchemaNode =
  | { kind: 'object'; properties: Record<string, ScopeSchemaNode> }
  | { kind: 'array'; element: ScopeSchemaNode }
  | { kind: 'primitive'; type: 'string' | 'number' | 'boolean' | 'null' | 'unknown' }
  // `params` and `returnType` are optional. With both absent this becomes
  // `() => unknown`. Each param's `type` and the `returnType` are TypeScript type
  // strings (for example `'string'`); when absent they default to `unknown`.
  | { kind: 'function'; params?: { name: string; type?: string }[]; returnType?: string }
  // A map with unknown keys, generated as Record<string, unknown>. Used when the
  // keys are decided at runtime (for example a resolved child instance whose
  // fields we cannot list ahead of time).
  | { kind: 'record' };

// Use a symbol from the global registry (Symbol.for) so it stays the same even
// if this file is loaded twice. A plain Symbol() would create a different symbol
// in each copy of the module, and the lookups would then miss each other.
export const CM_SCOPE_SCHEMA: unique symbol = Symbol.for(
  'unifyapps.cm.scopeSchema',
) as unknown as typeof CM_SCOPE_SCHEMA;

// Reads the schema a value has attached, if any. Checks that the value is an
// object or function before reading the symbol, so primitives and null do not
// throw.
export function getScopeSchema(value: unknown): ScopeSchemaNode | undefined {
  if (value === null || (typeof value !== 'object' && typeof value !== 'function')) {
    return undefined;
  }
  const node = (value as Record<symbol, unknown>)[CM_SCOPE_SCHEMA];
  return node === undefined ? undefined : (node as ScopeSchemaNode);
}
